/**
 * 에피소드 한 벌 — **판을 굴리는 데 필요한 것 전부.**
 *
 * ## 왜 이게 필요했나
 *
 * 지금까지 에피소드는 `level.ts` 의 `ACTIVE` 상수 하나로 갈렸다. 코드를 고쳐야
 * 바뀌었고, **한 서버가 한 사건만 알 수 있었다.** 방마다 다른 사건을 돌리려면
 * 모듈 최상단에서 읽던 것을 전부 「방이 들고 있는 값」으로 내려야 한다.
 *
 * 겁먹을 만한 리팩터로 보였는데 실제로 세어 보니 **서버가 쓰는 심볼은 열 개**였다.
 * 나머지는 전부 클라이언트가 렌더링에 쓰는 것이었다. 그래서 그 열 개만 이 한
 * 덩이로 묶는다.
 *
 * ## 규칙과 단서는 왜 갈라져 있나
 *
 * 여기(`shared/`)에 있는 것은 클라이언트도 봐도 되는 것이다 — 물건이 어디 있고
 * 방이 몇 개인지는 어차피 화면에 보인다. **단서 본문과 잠금 코드는 여기 없다.**
 * 그것은 `server/src/secrets-*.ts` 에 있고 클라이언트 번들에 절대 들어가지
 * 않는다. 그 경계가 이 게임의 전제다.
 */

import type { BodyPartDef, ForensicDef, LinkDef, LockDef, PropDef } from './level-defs';
import type { ObjState } from './protocol';
import type { RoundDef } from './rules';

export type EpisodeId = 'saholv7';

/** 어느 에피소드인지 모르는 값이 들어왔을 때 돌아갈 곳 */
export const DEFAULT_EPISODE: EpisodeId = 'saholv7';

export interface EpisodeRules {
  id: EpisodeId;
  PROP_BY_ID: Record<string, PropDef>;
  /** 물건이 들어 있는 칸 — `호스트키|부품`. 덮개를 열었는데 여기 없고 숨은 단서도 없으면 「먼지뿐이다」(2026-09-15) */
  INSIDE: Set<string>;
  LOCKS: Record<string, LockDef>;
  ROUNDS: RoundDef[];
  initialObjects(): Record<string, ObjState>;
  spawnPoint(index: number): [number, number, number];
  roundDef(round: number): RoundDef;
  investigateBlock(
    round: number,
    prop: { room: string; fromRound?: number; lateReason?: string },
    assignedZone: string | undefined,
    budgetLeft: number | undefined,
  ): string | null;
  squadBlock(round: number, out: string[], done: string[], playerId: string): string | null;
  readyPair(ids: string[], done: string[], pairing: Record<string, string>): [string, string] | null;
  newLinks(found: Record<string, unknown>): LinkDef[];
  /** 감정서 도착 규칙. 없으면 빈 배열 */
  FORENSICS: readonly ForensicDef[];
  /** 시신 부위 조사. 없으면 빈 배열 */
  BODY_PARTS: readonly BodyPartDef[];
}

/**
 * 두 에피소드를 **게으르게** 만든다.
 *
 * 최상단에서 둘 다 만들면 서버가 시작할 때 두 레벨의 모듈이 전부 평가된다.
 * 방 하나는 한 에피소드만 쓰므로 그건 낭비고, 에피소드가 백 개가 되면 낭비가
 * 아니라 문제가 된다. 처음 불릴 때 만들고 그다음부터는 재사용한다.
 */
const cache = new Map<EpisodeId, EpisodeRules>();

async function make(id: EpisodeId): Promise<EpisodeRules> {
  /**
   * **방의 에피소드 번들을 직접 받는다** — 배럴(`./level`)은 클라이언트가 고른 에피소드를 가리키는 전역이라
   * 여러 방을 돌리는 서버에서 쓰면 섞인다. 등록부는 순환 import 를 피하려고 여기서 늦게 부른다.
   */
  const { EPISODE_SOURCES } = await import('./episode-registry');
  const s = await EPISODE_SOURCES[id].level();
  return {
    id,
    PROP_BY_ID: s.PROP_BY_ID,
    INSIDE: new Set((s.PIECES as { inside?: { host: string; part?: string } }[]).filter((p) => p.inside).map((p) => `${p.inside!.host}|${p.inside!.part ?? ''}`)),
    LOCKS: s.LOCKS,
    ROUNDS: s.ROUNDS,
    initialObjects: s.initialObjects,
    spawnPoint: s.spawnPoint,
    roundDef: s.roundDef,
    investigateBlock: s.investigateBlock,
    squadBlock: s.squadBlock,
    readyPair: s.readyPair,
    newLinks: s.newLinks,
    FORENSICS: s.FORENSICS,
    BODY_PARTS: s.BODY_PARTS,
  };
}

export async function rulesFor(id: string | undefined): Promise<EpisodeRules> {
  const key: EpisodeId = EPISODE_CARDS.some((c) => c.id === id) ? (id as EpisodeId) : DEFAULT_EPISODE;
  let r = cache.get(key);
  if (!r) {
    r = await make(key);
    cache.set(key, r);
  }
  return r;
}

/** 로비가 고르는 목록. 여기 없으면 못 고른다 */
export interface EpisodeCard {
  id: EpisodeId;
  title: string;
  /** 한 줄 소개 — 카드에 걸린다 */
  lead: string;
  /** 인원 · 소요 시간처럼 고를 때 필요한 것 */
  meta: string;
  /** 아직 대본이 없으면 고를 수는 있되 그렇다고 말해 준다 */
  ready: boolean;
  note?: string;
}

export const EPISODE_CARDS: EpisodeCard[] = [
  /* 사흘 v7 — v8 판. 예전 `sahol` 카드는 배럴이 v7 로 바뀐 뒤에도 v3 비밀을 붙이는
   * 반쪽짜리였다(rules 는 DEFAULT_EPISODE 로 v7, secrets 는 id 그대로 v3).
   * 카드는 v7 만 걸고 `sahol` id 는 구 판 방 호환으로만 남긴다 (2026-09-22) */
  {
    id: 'saholv7',
    title: '사흘',
    lead: '눈에 갇힌 폐교. 죽은 지 사흘이 지났다는데, 다섯 사람 전부 어제 그와 이야기를 했다.',
    meta: '6인 · 100분',
    ready: true,
  },
];
